import React from "react";
import styled from "styled-components";
import Chart from "react-apexcharts";
import { numberFormat } from "helper/numberFormat";
import { formatDate } from "helper/dateFormat";
import { fontConfig } from "theme/fontConfig";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  gap: 16px;
  margin-bottom: 20px;
  @media (max-width: 1300px) { grid-template-columns: repeat(5, 1fr); }
  @media (max-width: 800px)  { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 500px)  { grid-template-columns: repeat(2, 1fr); }
`;
const StatusDot = styled.span`
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  margin-right: 5px;
  background: ${({ status }) =>
    status === "active"    ? "#22c55e" :
    status === "completed" ? "#2555ef" :
    status === "paused"    ? "#f59e0b" : "#9ca3af"};
`;
const StatCard = styled.div`
  background: #f3f6f9;
  border-radius: 10px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 90px;
`;
const StatValue = styled.div`
  font-family: var(--FundraisingStatValueFont-family);
  font-size: 20px;
  font-weight: 700;
  color: #2555ef;
  line-height: 1.2;
  text-align: center;
  display: flex;
  align-items: center;
`;
const StatLabel = styled.div`
  font-family: var(--FundraisingStatLabelFont-family);
  font-size: 12px;
  color: #4d5565;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 500;
`;
const Section = styled.div`
  background: #f3f6f9;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
`;
const SectionTitle = styled.div`
  font-family: var(--FundraisingSectionTitleFont-family);
  font-size: 13px;
  font-weight: 600;
  color: #002e5c;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.7;
  margin-bottom: 14px;
`;
const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 100px;
  background: #e5e7eb;
  overflow: hidden;
  margin-bottom: 8px;
`;
const ProgressFill = styled.div`
  height: 100%;
  border-radius: 100px;
  background: linear-gradient(90deg, #2555ef, #5b8af5);
  width: ${({ pct }) => Math.min(100, pct)}%;
`;
const ProgressMeta = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: var(--FundraisingProgressMetaFont-family);
  font-size: 13px;
  color: #4d5565;
`;
const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;
const DonorRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e5e7eb;
  font-family: var(--FundraisingDonorRowFont-family);
  font-size: 14px;
  color: #4d5565;
  &:last-child { border-bottom: none; }
`;
const DonorAmount = styled.span`
  font-weight: 600;
  color: #2555ef;
`;

const Analytics = ({ analytics }) => {
  if (!analytics) return null;

  const {
    goal_amount = 0,
    raised_amount = 0,
    donor_count = 0,
    avg_gift = 0,
    recurring_count = 0,
    percent_complete = 0,
    end_date,
    status,
    daily_series = [],
    top_donors = [],
  } = analytics;

  const fmtDate = d => d ? formatDate({ date: d }) : "—";

  const latestDay = daily_series.length > 0 ? daily_series[daily_series.length - 1] : null;
  const today_amount = latestDay?.total ?? 0;

  const chartCategories = daily_series.map(d => formatDate({ date: d._id }));
  const amountSeries = daily_series.map(d => d.total ?? 0);
  const countSeries  = daily_series.map(d => d.count ?? 0);

  return (
    <div>
      {/* KPI row */}
      <Grid>
        <StatCard>
          <StatValue>{numberFormat({ number: goal_amount, decimal: 0, style: "currency" })}</StatValue>
          <StatLabel>Goal</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{numberFormat({ number: raised_amount, decimal: 0, style: "currency" })}</StatValue>
          <StatLabel>Raised</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{donor_count}</StatValue>
          <StatLabel>Donors</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{percent_complete}%</StatValue>
          <StatLabel>Complete</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{fmtDate(end_date)}</StatValue>
          <StatLabel>End Date</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>
            <StatusDot status={status} />
            {status ? status.charAt(0).toUpperCase() + status.slice(1) : "—"}
          </StatValue>
          <StatLabel>Status</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{numberFormat({ number: today_amount, decimal: 0, style: "currency" })}</StatValue>
          <StatLabel>Today</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{numberFormat({ number: avg_gift, decimal: 0, style: "currency" })}</StatValue>
          <StatLabel>Avg Gift</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{recurring_count}</StatValue>
          <StatLabel>Recurring</StatLabel>
        </StatCard>
      </Grid>

      {/* Progress bar */}
      <Section>
        <SectionTitle>Progress to Goal</SectionTitle>
        <ProgressTrack>
          <ProgressFill pct={percent_complete} />
        </ProgressTrack>
        <ProgressMeta>
          <span>{numberFormat({ number: raised_amount, decimal: 0, style: "currency" })} raised</span>
          <span>Goal: {numberFormat({ number: goal_amount, decimal: 0, style: "currency" })}</span>
        </ProgressMeta>
      </Section>

      {/* Donors list + Daily chart */}
      <TwoCol>
        {top_donors.length > 0 && (
          <Section style={{ marginBottom: 0 }}>
            <SectionTitle>Top Donors</SectionTitle>
            {top_donors.map((d, i) => (
              <DonorRow key={i}>
                <span>{i + 1}. {d._id || "Anonymous Donor"}</span>
                <DonorAmount>{numberFormat({ number: d.total, decimal: 0, style: "currency" })}</DonorAmount>
              </DonorRow>
            ))}
          </Section>
        )}

        <Section style={{ marginBottom: 0 }}>
          <SectionTitle>Daily Activity</SectionTitle>
          {daily_series.length > 0 ? (
            <Chart
              type="line"
              height={280}
              series={[
                { name: "Amount", type: "bar",  data: amountSeries },
                { name: "Donations", type: "line", data: countSeries },
              ]}
              options={{
                chart: { toolbar: { show: false }, fontFamily: fontConfig.FundraisingChartFont.family },
                xaxis: {
                  categories: chartCategories,
                  labels: { style: { fontFamily: fontConfig.FundraisingChartFont.family, fontSize: "11px" }, rotate: -30 },
                },
                yaxis: [
                  {
                    title: { text: "Amount ($)", style: { fontFamily: fontConfig.FundraisingChartFont.family, fontSize: "11px" } },
                    labels: { formatter: v => `$${v}`, style: { fontFamily: fontConfig.FundraisingChartFont.family, fontSize: "11px" } },
                  },
                  {
                    opposite: true,
                    title: { text: "# Donations", style: { fontFamily: fontConfig.FundraisingChartFont.family, fontSize: "11px" } },
                    labels: { formatter: v => Math.round(v), style: { fontFamily: fontConfig.FundraisingChartFont.family, fontSize: "11px" } },
                  },
                ],
                tooltip: {
                  y: [
                    { formatter: v => numberFormat({ number: v, decimal: 0, style: "currency" }) },
                    { formatter: v => `${v} donation${v !== 1 ? "s" : ""}` },
                  ],
                },
                colors: ["#2555ef", "#22c55e"],
                plotOptions: { bar: { borderRadius: 4 } },
                dataLabels: { enabled: false },
                grid: { borderColor: "#e5e7eb" },
                stroke: { width: [0, 2.5], curve: "smooth" },
                markers: { size: [0, 5], strokeWidth: 2, strokeColors: "#fff" },
                fill: { opacity: [0.85, 1] },
                legend: { fontFamily: fontConfig.FundraisingChartFont.family, fontSize: "12px" },
              }}
            />
          ) : (
            <div style={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontFamily: fontConfig.FundraisingAnalyticsTextFont.family, fontSize: 14 }}>
              No donations yet
            </div>
          )}
        </Section>
      </TwoCol>
    </div>
  );
};

export default Analytics;
